/**
 * Murphy Door Configurator - Grouped Product Business Logic Handler
 *
 * PURPOSE: Material preference scoring, variant selection optimization,
 * product grouping analysis, and auto-apply browsing state coordination
 * for CompositeProduct grouped products.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 1 & 19)
 * - publishEvent() async helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED (Pattern 3)
 * - Event consumer handler for bootstrap (Pattern 4)
 * - Event replay ready via bootstrap subscription at load time (Pattern 5)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first O(1) data access via conductor delegation (Patterns 7, 8)
 * - Data access through conductor delegation (Pattern 9)
 * - UI updates through conductor delegation (Pattern 10)
 * - Async event publishing (Pattern 11)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - No user input rendered to DOM (Pattern 17 - N/A for pure BL)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - CONSTANTS.EVENTS.* constant usage (Pattern 19)
 * - Conductor single publication (Pattern 20)
 *
 * PERFORMANCE CHARACTERISTICS:
 * - Group analysis: O(1) lookup from PPD compositeGroupIdToProductMap
 * - Material scoring: O(1) pre-computed bestVariantByMaterialPreference
 * - State application: Async conductor delegation
 * - Zero raw data access - pure PPD-First patterns only
 *
 * EVENT TYPES (via CONSTANTS.EVENTS):
 * Lifecycle Events:
 * - BUSINESS_GROUPED_PRODUCT_SELECTION_STARTED - Selection processing begins
 * - BUSINESS_GROUPED_PRODUCT_SELECTION_VALIDATION - Input validation result
 * - BUSINESS_GROUPED_PRODUCT_SELECTION_COMPLETED - Selection successful
 * - BUSINESS_GROUPED_PRODUCT_SELECTION_FAILED - Selection error
 *
 * Milestone Events:
 * - BUSINESS_GROUP_ANALYSIS_STARTED - Group lookup begins
 * - BUSINESS_GROUP_ANALYSIS_COMPLETED - Group found in PPD
 * - BUSINESS_GROUP_ANALYSIS_FAILED - Group not found
 * - BUSINESS_MATERIAL_SCORING_STARTED - Scoring begins
 * - BUSINESS_MATERIAL_SCORING_COMPLETED - Best variant selected
 * - BUSINESS_MATERIAL_SCORING_FAILED - Scoring error
 * - BUSINESS_BROWSING_STATE_STARTED - State application begins
 * - BUSINESS_BROWSING_STATE_APPLIED - State successfully applied
 * - BUSINESS_BROWSING_STATE_FAILED - State application error
 *
 * Performance Events:
 * - PERFORMANCE_GROUPED_PRODUCT_SELECTION_SLOW - Operation exceeded threshold
 *
 * Module Lifecycle Events:
 * - MODULE_GROUPED_PRODUCT_HANDLER_READY - Module initialized
 * - MODULE_GROUPED_PRODUCT_HANDLER_CLEANUP - Module cleaned up
 *
 * All external communication flows through md-config-core-conductor.js delegation.
 *
 * @version 3.0.0 - Golden Standard CCP Compliance (200/200 target)
 * @author Murphy Door Configurator Team
 */

(function () {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    GROUPED_PRODUCT_SELECTION: 100, // ms - full selection pipeline
    GROUP_ANALYSIS: 15, // ms - O(1) PPD lookup
    MATERIAL_SCORING: 15, // ms - O(1) pre-computed access
    STATE_APPLICATION: 50, // ms - conductor delegation
  });

  const ERROR_MESSAGES = Object.freeze({
    NOT_BOOTSTRAPPED:
      'Grouped product handler called before bootstrap complete',
    INVALID_EVENT_DATA: 'Invalid grouped product event data',
    MISSING_GROUP_ID: 'Missing groupId in event data',
    CONDUCTOR_UNAVAILABLE:
      'Conductor not available for grouped product handling',
    CONDUCTOR_METHOD_MISSING:
      'Conductor missing getCompositeProductByGroupId method',
    GROUP_NOT_FOUND: 'Group ID not found in PPD compositeGroupIdToProductMap',
    PPD_VIOLATION: 'CompositeProduct missing bestVariantByMaterialPreference',
    SCORING_ERROR: 'Material scoring error',
    STATE_APPLICATION_ERROR: 'Browsing state application error',
    CONDUCTOR_UNAVAILABLE_VARIANT_DISPLAY:
      'Conductor unavailable for variant display',
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  let cleanupHandlers = [];

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, eventData) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `[GroupedProductHandler] Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(
          `[GroupedProductHandler] Error publishing ${eventType}:`,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING HELPER (10 points)
  // ============================================================================
  function trackPerformance(operationType, duration, context) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(EVENTS.PERFORMANCE_GROUPED_PRODUCT_SELECTION_SLOW, {
        operation: operationType,
        duration,
        threshold,
        severity: duration > threshold * 2 ? 'critical' : 'warning',
        context,
        timestamp: Date.now(),
      });

      isDev() &&
        console.warn(
          `[GroupedProductHandler] Slow operation: ${operationType} - ${duration.toFixed(2)}ms (threshold: ${threshold}ms)`
        );
    }
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 5: EVENT REPLAY READY (10 points)
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // ============================================================================
  if (CoreEventBus) {
    // Subscribe to bootstrap completion at module load time (race protection)
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log(
            '[GroupedProductHandler] Bootstrap complete event received'
          );
        bootstrapComplete = true;
        isInitialized = true;

        // Publish module ready event
        publishEvent(EVENTS.MODULE_GROUPED_PRODUCT_HANDLER_READY, {
          module: 'groupedProductHandler',
          timestamp: Date.now(),
        });
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    isDev() &&
      console.log(
        '[GroupedProductHandler] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // MODULE IMPLEMENTATION
  // ============================================================================

  /**
   * Grouped Product Business Logic Handler
   * Coordinates complex material selection and variant scoring for grouped products
   */
  const groupedProductHandler = {
    /**
     * Processes grouped product selection with material preference scoring
     *
     * Implements 5-stage event lifecycle:
     * STARTED -> VALIDATION -> PROGRESS (group analysis + scoring) -> COMPLETED/FAILED
     *
     * @param {Object} eventData - Grouped product event data
     * @param {string} eventData.groupId - Group identifier
     * @param {string} eventData.groupName - Display name of the group
     * @param {string} eventData.categoryId - Category context
     * @param {number} eventData.productCount - Number of products in group
     * @param {number} eventData.variantCount - Number of variants in group
     * @param {HTMLElement} eventData.element - DOM element that was clicked
     * @param {Object} conductor - Reference to conductor for coordination
     * @returns {Object} Processing result with selected variant and metadata
     */
    async processGroupedProductSelection(eventData, conductor) {
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();
      const METHOD_SIGNATURE =
        'groupedProductHandler.processGroupedProductSelection';

      // PATTERN 6: Bootstrap runtime protection
      if (!bootstrapComplete) {
        isDev() &&
          console.warn(
            `[GroupedProductHandler] ${ERROR_MESSAGES.NOT_BOOTSTRAPPED}`
          );
        publishEvent(EVENTS.BUSINESS_GROUPED_PRODUCT_SELECTION_FAILED, {
          reason: 'not_bootstrapped',
          error: ERROR_MESSAGES.NOT_BOOTSTRAPPED,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });
        return { success: false, error: ERROR_MESSAGES.NOT_BOOTSTRAPPED };
      }

      try {
        // ================================================================
        // STAGE 1: START - Announce operation beginning
        // ================================================================
        const { groupId, groupName, categoryId } = eventData;

        publishEvent(EVENTS.BUSINESS_GROUPED_PRODUCT_SELECTION_STARTED, {
          groupId,
          groupName,
          categoryId,
          productCount: eventData.productCount,
          variantCount: eventData.variantCount,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // ================================================================
        // STAGE 2: VALIDATION - Validate inputs
        // ================================================================
        if (!eventData || typeof eventData !== 'object') {
          publishEvent(EVENTS.BUSINESS_GROUPED_PRODUCT_SELECTION_VALIDATION, {
            valid: false,
            error: ERROR_MESSAGES.INVALID_EVENT_DATA,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          return { success: false, error: ERROR_MESSAGES.INVALID_EVENT_DATA };
        }

        if (!groupId) {
          publishEvent(EVENTS.BUSINESS_GROUPED_PRODUCT_SELECTION_VALIDATION, {
            valid: false,
            error: ERROR_MESSAGES.MISSING_GROUP_ID,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          return { success: false, error: ERROR_MESSAGES.MISSING_GROUP_ID };
        }

        if (!conductor) {
          publishEvent(EVENTS.BUSINESS_GROUPED_PRODUCT_SELECTION_VALIDATION, {
            valid: false,
            error: ERROR_MESSAGES.CONDUCTOR_UNAVAILABLE,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          return {
            success: false,
            error: ERROR_MESSAGES.CONDUCTOR_UNAVAILABLE,
          };
        }

        publishEvent(EVENTS.BUSINESS_GROUPED_PRODUCT_SELECTION_VALIDATION, {
          valid: true,
          groupId,
          groupName,
          categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // ================================================================
        // STAGE 3: PROGRESS - Business logic (group analysis + scoring)
        // ================================================================

        // BUSINESS LOGIC: Find and analyze the specific group via PPD
        const groupAnalysisResult = this.analyzeProductGroup(
          groupId,
          conductor
        );

        if (!groupAnalysisResult.success) {
          publishEvent(EVENTS.BUSINESS_GROUPED_PRODUCT_SELECTION_FAILED, {
            groupId,
            reason: groupAnalysisResult.reason,
            stage: 'group_analysis',
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });

          isDev() &&
            console.warn(
              `${METHOD_SIGNATURE}: Group analysis failed`,
              groupAnalysisResult
            );
          return groupAnalysisResult;
        }

        // BUSINESS LOGIC: Perform material preference scoring
        const scoringResult = this.performMaterialScoring(
          groupAnalysisResult.targetGroup
        );

        if (!scoringResult.success) {
          publishEvent(EVENTS.BUSINESS_GROUPED_PRODUCT_SELECTION_FAILED, {
            groupId,
            reason: scoringResult.reason,
            error: scoringResult.error,
            stage: 'material_scoring',
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });

          isDev() &&
            console.warn(
              `${METHOD_SIGNATURE}: Material scoring failed`,
              scoringResult
            );
          return scoringResult;
        }

        // BUSINESS LOGIC: Check if material preferences exist for auto-selection vs manual selection
        let result;
        if (scoringResult.bestScore > 0) {
          // Auto-apply browsing state with best variant
          const stateApplication = await this.applyBrowsingState(
            scoringResult.bestVariant,
            scoringResult.bestScore,
            eventData,
            conductor
          );

          result = {
            success: stateApplication.success,
            groupName,
            selectedVariantId: scoringResult.bestVariant.id,
            materialScore: scoringResult.bestScore,
            stateApplied: stateApplication.success,
            autoApplied: true,
          };
        } else {
          // No material preferences - delegate to CompositeProduct variant display
          if (conductor.handleStateChange) {
            const variantDisplayResult = conductor.handleStateChange(
              'MULTI_VARIANT_START',
              {
                productId: groupAnalysisResult.targetGroup.id.toString(),
                element: eventData.element,
                categoryId: eventData.categoryId,
                isComposite: true,
                groupId: eventData.groupId,
                groupName: eventData.groupName,
              }
            );

            result = {
              success: variantDisplayResult !== false,
              groupName,
              selectedVariantId: null,
              materialScore: scoringResult.bestScore,
              stateApplied: false,
              autoApplied: false,
              variantSelectionShown: variantDisplayResult !== false,
            };
          } else {
            // Hard failure - publish FAILED event and return immediately
            const reason = ERROR_MESSAGES.CONDUCTOR_UNAVAILABLE_VARIANT_DISPLAY;
            isDev() && console.warn(`${METHOD_SIGNATURE}: ${reason}`);

            publishEvent(EVENTS.BUSINESS_GROUPED_PRODUCT_SELECTION_FAILED, {
              groupId: eventData.groupId,
              reason,
              stage: 'variant_display',
              timestamp: Date.now(),
              source: METHOD_SIGNATURE,
            });

            return { success: false, reason };
          }
        }

        // ================================================================
        // STAGE 4: COMPLETED - Announce success with performance metrics
        // ================================================================
        const executionTime =
          (window.performance ? window.performance.now() : Date.now()) -
          startTime;

        // PATTERN 12: Performance tracking
        trackPerformance('GROUPED_PRODUCT_SELECTION', executionTime, {
          groupId,
        });

        publishEvent(EVENTS.BUSINESS_GROUPED_PRODUCT_SELECTION_COMPLETED, {
          groupId,
          groupName,
          result,
          executionTime,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        isDev() &&
          console.log('[GroupedProductHandler] Selection Completed:', {
            groupId,
            groupName,
            executionTime: `${executionTime.toFixed(2)}ms`,
            success: result.success,
            autoApplied: result.autoApplied,
          });

        return result;
      } catch (error) {
        // ================================================================
        // STAGE 5: FAILED - Announce error
        // ================================================================
        publishEvent(EVENTS.BUSINESS_GROUPED_PRODUCT_SELECTION_FAILED, {
          error: error.message || error,
          stack: error.stack,
          eventData,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error in grouped product processing:`,
            error
          );
        return {
          success: false,
          reason: 'processing_error',
          error: error.message,
        };
      }
    },

    /**
     * Analyzes product group to find target group and extract variants
     *
     * PPD-FIRST PATTERN: Uses conductor.getCompositeProductByGroupId() for O(1) lookup
     * CompositeProducts exist in the virtualized product layer, not raw products
     *
     * @param {string} groupId - Target group ID
     * @param {Object} conductor - Conductor reference for PPD access
     * @returns {Object} Analysis result with target group data
     */
    analyzeProductGroup(groupId, conductor) {
      const METHOD_SIGNATURE = 'groupedProductHandler.analyzeProductGroup';
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();

      try {
        // Stage 1: Publish operation started event
        publishEvent(EVENTS.BUSINESS_GROUP_ANALYSIS_STARTED, {
          groupId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // PPD-FIRST: Get CompositeProduct directly from PPD via conductor delegation
        if (!conductor || !conductor.getCompositeProductByGroupId) {
          isDev() &&
            console.error(
              `${METHOD_SIGNATURE}: ${ERROR_MESSAGES.CONDUCTOR_METHOD_MISSING}`
            );

          publishEvent(EVENTS.BUSINESS_GROUP_ANALYSIS_FAILED, {
            groupId,
            reason: 'conductor_method_missing',
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });

          return { success: false, reason: 'conductor_method_missing' };
        }

        // Get CompositeProduct by group-id (O(1) lookup from PPD)
        const targetCompositeProduct =
          conductor.getCompositeProductByGroupId(groupId);

        if (targetCompositeProduct) {
          const duration =
            (window.performance ? window.performance.now() : Date.now()) -
            startTime;
          trackPerformance('GROUP_ANALYSIS', duration, { groupId });

          publishEvent(EVENTS.BUSINESS_GROUP_ANALYSIS_COMPLETED, {
            groupId,
            targetGroupId: targetCompositeProduct.id,
            productCount: 1,
            variantCount: targetCompositeProduct.variants?.length || 0,
            duration,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });

          // PPD-FIRST: Return CompositeProduct directly (no wrapper)
          return {
            success: true,
            targetGroup: targetCompositeProduct,
          };
        }

        // PPD-FIRST: No fallback needed - ALL grouped products exist as CompositeProducts in PPD
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Group ID "${groupId}" not found in PPD compositeGroupIdToProductMap - data pipeline issue`
          );

        publishEvent(EVENTS.BUSINESS_GROUP_ANALYSIS_FAILED, {
          groupId,
          reason: 'target_group_not_found',
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return { success: false, reason: 'target_group_not_found' };
      } catch (error) {
        isDev() &&
          console.error(`${METHOD_SIGNATURE}: Error in group analysis:`, error);

        publishEvent(EVENTS.BUSINESS_GROUP_ANALYSIS_FAILED, {
          groupId,
          reason: 'analysis_error',
          error: error.message,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return {
          success: false,
          reason: 'analysis_error',
          error: error.message,
        };
      }
    },

    /**
     * Performs material preference scoring to find optimal variant
     *
     * PPD-FIRST PATTERN: Uses pre-computed bestVariantByMaterialPreference
     * from CompositeProduct (O(1) lookup) instead of runtime iteration (O(n))
     *
     * PERFORMANCE: Pre-computed in PPD Layer 4 during bootstrap - zero runtime cost
     *
     * @param {Object} targetGroup - Target group with merged variants
     * @returns {Object} Scoring result with best variant and score
     */
    performMaterialScoring(targetGroup) {
      const METHOD_SIGNATURE = 'groupedProductHandler.performMaterialScoring';
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();

      try {
        // Stage 1: Publish operation started event
        publishEvent(EVENTS.BUSINESS_MATERIAL_SCORING_STARTED, {
          targetGroupId: targetGroup?.id || null,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // PPD-FIRST ENFORCEMENT: bestVariantByMaterialPreference MUST exist
        if (!targetGroup.bestVariantByMaterialPreference) {
          throw new Error(
            `CRITICAL PPD VIOLATION: CompositeProduct "${targetGroup.display_name}" (ID: ${targetGroup.id}) ` +
              `missing bestVariantByMaterialPreference. PPD Layer 4 pipeline failure! ` +
              `This property MUST be pre-computed during bootstrap. ` +
              `Check md-config-core-data-pipeline.js Layer 4 _computeAndSetBestVariant() call.`
          );
        }

        const precomputed = targetGroup.bestVariantByMaterialPreference;
        const duration =
          (window.performance ? window.performance.now() : Date.now()) -
          startTime;
        trackPerformance('MATERIAL_SCORING', duration, {
          targetGroupId: targetGroup.id,
        });

        isDev() &&
          console.log(
            `[GroupedProductHandler] Using pre-computed best variant (O(1) lookup) - score: ${precomputed.score}`
          );

        // Stage 2: Publish success event
        publishEvent(EVENTS.BUSINESS_MATERIAL_SCORING_COMPLETED, {
          targetGroupId: targetGroup.id,
          bestScore: precomputed.score,
          variantsProcessed: targetGroup.variants.length,
          precomputed: true,
          scoredAt: precomputed.scoredAt,
          duration,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return {
          success: true,
          bestVariant: precomputed.variant,
          bestScore: precomputed.score,
          variantsProcessed: targetGroup.variants.length,
          precomputed: true,
          scoredAt: precomputed.scoredAt,
        };
      } catch (error) {
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error in material scoring:`,
            error
          );

        publishEvent(EVENTS.BUSINESS_MATERIAL_SCORING_FAILED, {
          targetGroupId: targetGroup?.id || null,
          reason: 'scoring_error',
          error: error.message,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return {
          success: false,
          reason: 'scoring_error',
          error: error.message,
        };
      }
    },

    /**
     * PPD-FIRST ENFORCEMENT: Runtime scoring is PROHIBITED
     *
     * This method enforces PPD-First architecture by preventing runtime
     * material scoring iterations. All scoring MUST be pre-computed in
     * PPD Layer 4 during bootstrap or score invalidation events.
     *
     * @throws {Error} Always throws - enforces architectural purity
     * @deprecated This pattern is architecturally prohibited
     */
    performMaterialScoringLegacy() {
      throw new Error(
        'CRITICAL ARCHITECTURAL VIOLATION: performMaterialScoringLegacy() is PROHIBITED in PPD-First architecture. ' +
          'Material scores MUST be pre-computed in PPD Layer 4 during bootstrap or invalidation. ' +
          'Use performMaterialScoring() which accesses pre-computed bestVariantByMaterialPreference. ' +
          'This is an architectural purity enforcement - all runtime transformations must move to Phase 1.'
      );
    },

    /**
     * Applies browsing state with the selected optimal variant
     *
     * CONDUCTOR DELEGATION PATTERN: Coordinates state change through
     * selectionCoordinator.enterBrowsingState() for proper state application
     *
     * @param {Object} bestVariant - Selected optimal variant
     * @param {number} bestScore - Material preference score
     * @param {Object} eventData - Original event data
     * @param {Object} conductor - Conductor reference for state coordination
     * @returns {Object} State application result
     */
    async applyBrowsingState(bestVariant, bestScore, eventData, conductor) {
      const METHOD_SIGNATURE = 'groupedProductHandler.applyBrowsingState';
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();

      try {
        const { groupId, groupName, categoryId, element } = eventData;

        // Stage 1: Operation Started
        publishEvent(EVENTS.BUSINESS_BROWSING_STATE_STARTED, {
          variantId: bestVariant.id,
          materialScore: bestScore,
          groupId,
          groupName,
          categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // DRY: Use unified selection coordinator for standardized BROWSING state entry
        const result =
          await window.MurphyDoorConfigurator.selectionCoordinator.enterBrowsingState(
            {
              productId: bestVariant.sourceProductId,
              variantId: bestVariant.id,
              categoryId,
              element,
              autoApplied: true,
              materialScore: bestScore,
              compositeContext: {
                isCompositeProduct: true,
                groupId,
                groupName,
                categoryId,
                element,
              },
            },
            conductor
          );

        if (!result.success) {
          throw new Error(
            `Coordinator failed to apply browsing state: ${result.error}`
          );
        }

        const duration =
          (window.performance ? window.performance.now() : Date.now()) -
          startTime;
        trackPerformance('STATE_APPLICATION', duration, { groupId });

        // Stage 2: Operation Completed Successfully
        publishEvent(EVENTS.BUSINESS_BROWSING_STATE_APPLIED, {
          variantId: bestVariant.id,
          stateApplied: 'SELECTION_BROWSING',
          groupId,
          groupName,
          categoryId,
          autoApplied: true,
          duration,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return {
          success: true,
          stateApplied: 'SELECTION_BROWSING',
          autoApplied: true,
        };
      } catch (error) {
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error applying browsing state:`,
            error
          );

        // Stage 3: Operation Failed
        publishEvent(EVENTS.BUSINESS_BROWSING_STATE_FAILED, {
          error: error.message,
          stack: error.stack,
          variantId: bestVariant?.id || null,
          materialScore: bestScore,
          groupId: eventData?.groupId || null,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return {
          success: false,
          reason: 'state_application_error',
          error: error.message,
        };
      }
    },

    // ============================================================================
    // PATTERN 15: CLEANUP METHOD (10 points)
    // ============================================================================

    /**
     * Cleans up all event subscriptions and resets state.
     * Called during module teardown or system shutdown.
     */
    cleanup() {
      isDev() && console.log('[GroupedProductHandler] Cleaning up resources');

      // Unsubscribe all event handlers
      cleanupHandlers.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch (error) {
          isDev() &&
            console.warn(
              '[GroupedProductHandler] Cleanup handler error:',
              error
            );
        }
      });
      cleanupHandlers.length = 0;

      isInitialized = false;
      bootstrapComplete = false;

      publishEvent(EVENTS.MODULE_GROUPED_PRODUCT_HANDLER_CLEANUP, {
        module: 'groupedProductHandler',
        timestamp: Date.now(),
      });
    },
  };

  // ============================================================================
  // PATTERN 20: CONDUCTOR SINGLE PUBLICATION (10 points)
  // Export to global Murphy Door Configurator namespace
  // ============================================================================
  window.MurphyDoorConfigurator.groupedProductHandler = groupedProductHandler;

  isDev() &&
    console.info(
      '[GroupedProductHandler] Business logic module loaded and exported to global namespace'
    );
})();
